/**
 * Fluxonix RFQ Bundling Intelligence Agent (UI Component)
 * Author: Fluxonix
 * 
 * Notes:
 * - Pulls RFQs from backend (/api/rfq/list) if available, otherwise uses mock.
 * - Calls Fluxonix inference interface (/api/agent/run or /api/infer)
 *   which automatically switches between OpenAI/Ollama based on tenant settings.
 * - Multi-tenant friendly: backend already knows tenant slug from auth context.
 */

import React, { useState, useEffect } from "react";
import {
  ChevronRight,
  Package,
  TrendingDown,
  Layers,
  Sparkles,
  DollarSign,
  MapPin,
  Calendar,
  CheckCircle,
} from "lucide-react";
import { api } from "../api"; // Fluxonix authenticated API wrapper

// ============================================================
// FLUXONIX BRANDING (inline styles)
// ============================================================

const colors = {
  primary: "#5B4BFF",
  primaryDark: "#4338CA",
  secondary: "#8B5CF6",
  gradient: "linear-gradient(135deg, #5B4BFF, #8B5CF6)",
  bgSoft: "#F7F7FC",
  textDark: "#111827",
  textSoft: "#6B7280",
  success: "#22C55E",
  warning: "#F59E0B",
  danger: "#EF4444",
};

const fx = {
  page: {
    minHeight: "100vh",
    padding: "50px 20px",
    background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
    fontFamily: "Inter, sans-serif",
  },
  card: {
    maxWidth: "1400px",
    margin: "0 auto",
    background: "#fff",
    padding: "45px",
    borderRadius: "24px",
    boxShadow: "0 15px 40px rgba(80, 70, 200, 0.15)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "40px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  h1: {
    fontSize: "32px",
    fontWeight: 800,
    color: colors.textDark,
    margin: 0,
  },
  subtitle: {
    fontSize: "16px",
    color: colors.textSoft,
    margin: 0,
  },
  button: {
    padding: "15px 32px",
    fontSize: "16px",
    fontWeight: 700,
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    color: "#fff",
    background: colors.gradient,
    boxShadow: "0 8px 20px rgba(91,75,255,0.35)",
    transition: "all 0.25s",
  },
  buttonDisabled: {
    padding: "15px 32px",
    fontSize: "16px",
    fontWeight: 700,
    borderRadius: "12px",
    border: "none",
    cursor: "not-allowed",
    color: "#fff",
    background: "#CBD5E1",
    opacity: 0.6,
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "32px",
  },
  panelRFQ: {
    background: colors.bgSoft,
    padding: "28px",
    borderRadius: "16px",
    border: "1px solid #E5E7EB",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
  },
  panelBundles: {
    background: "linear-gradient(135deg, #FEF2F2, #FEE2E2)",
    padding: "28px",
    borderRadius: "16px",
    border: "1px solid #FECACA",
    boxShadow: "0 4px 12px rgba(239,68,68,0.1)",
  },
  panelTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: colors.textDark,
    marginTop: 0,
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  panelTitleDanger: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#B91C1C",
    marginTop: 0,
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  rfqCard: {
    marginBottom: "14px",
    padding: "16px",
    background: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    transition: "all 0.2s",
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
  },
  bundleCard: {
    marginBottom: "16px",
    padding: "20px",
    background: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #FECACA",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 2px 8px rgba(239,68,68,0.08)",
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: "15px",
    color: colors.textDark,
    marginBottom: "4px",
  },
  cardSubtitle: {
    fontSize: "13px",
    color: colors.textSoft,
    marginBottom: "10px",
  },
  metaRow: {
    display: "flex",
    gap: "16px",
    fontSize: "12px",
    color: colors.textSoft,
    flexWrap: "wrap",
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  statsGrid: {
    marginTop: "14px",
    fontSize: "13px",
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "14px",
  },
  statBox: {
    display: "flex",
    flexDirection: "column",
  },
  statLabel: {
    fontSize: "11px",
    color: "#94a3b8",
    fontWeight: 600,
    textTransform: "uppercase",
    marginBottom: "4px",
  },
  statValue: (color) => ({
    color: color || colors.primary,
    fontWeight: 700,
    fontSize: "15px",
  }),
  expandedSection: {
    marginTop: "18px",
    paddingTop: "18px",
    borderTop: "1px solid #E5E7EB",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "14px",
    marginBottom: "8px",
    color: colors.textDark,
  },
  list: {
    margin: "8px 0",
    paddingLeft: "20px",
  },
  listItem: {
    fontSize: "13px",
    color: colors.textDark,
    marginBottom: "6px",
  },
  emptyState: {
    padding: "48px 32px",
    textAlign: "center",
    color: "#B91C1C",
    fontSize: "15px",
  },
};

// --------------------------------------------
// 1. Expanded Mock RFQs (10 realistic examples)
// --------------------------------------------
const generateMockRFQs = () => [
  {
    id: "RFQ-2025-001",
    component: "Seat Belt Webbing",
    category: "Textiles",
    quantity: 50000,
    estimatedValue: 125000,
    supplier: "Supplier A",
    location: "Germany",
    deadline: "2025-02-15",
  },
  {
    id: "RFQ-2025-002",
    component: "Retractor Spring",
    category: "Metal Components",
    quantity: 42000,
    estimatedValue: 83000,
    supplier: "Supplier B",
    location: "Germany",
    deadline: "2025-02-20",
  },
  {
    id: "RFQ-2025-003",
    component: "Airbag Fabric Panel",
    category: "Textiles",
    quantity: 30000,
    estimatedValue: 180000,
    supplier: "Supplier A",
    location: "Germany",
    deadline: "2025-02-18",
  },
  {
    id: "RFQ-2025-004",
    component: "Plastic Trim Housing",
    category: "Plastic Components",
    quantity: 60000,
    estimatedValue: 90000,
    supplier: "Supplier C",
    location: "Poland",
    deadline: "2025-03-01",
  },
  {
    id: "RFQ-2025-005",
    component: "Gas Generator Cartridge",
    category: "Pyrotechnics",
    quantity: 45000,
    estimatedValue: 225000,
    supplier: "Supplier D",
    location: "USA",
    deadline: "2025-02-25",
  },
  {
    id: "RFQ-2025-006",
    component: "Anchor Plate",
    category: "Metal Components",
    quantity: 55000,
    estimatedValue: 82500,
    supplier: "Supplier B",
    location: "Germany",
    deadline: "2025-02-22",
  },
  {
    id: "RFQ-2025-007",
    component: "Steering Wheel Leather Wrap",
    category: "Textiles",
    quantity: 25000,
    estimatedValue: 150000,
    supplier: "Supplier E",
    location: "Mexico",
    deadline: "2025-03-05",
  },
  {
    id: "RFQ-2025-008",
    component: "Inflator Connector",
    category: "Electrical",
    quantity: 40000,
    estimatedValue: 60000,
    supplier: "Supplier F",
    location: "China",
    deadline: "2025-02-27",
  },
  {
    id: "RFQ-2025-009",
    component: "ABS Module Cover",
    category: "Plastic Components",
    quantity: 32000,
    estimatedValue: 72000,
    supplier: "Supplier C",
    location: "Poland",
    deadline: "2025-03-03",
  },
  {
    id: "RFQ-2025-010",
    component: "Load Limiter Bar",
    category: "Metal Components",
    quantity: 48000,
    estimatedValue: 96000,
    supplier: "Supplier B",
    location: "Germany",
    deadline: "2025-02-24",
  },
];

// -----------------------------
// 2. Fluxonix AI Agent UI Component
// -----------------------------
const RFQBundlingAgent = () => {
  const [rfqs, setRfqs] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);

  // -----------------------------
  // Load RFQs from backend OR fallback immediately to mock RFQs
  // -----------------------------
  useEffect(() => {
  const loadRFQs = async () => {
    try {
      const res = await api.get("/api/rfq/list");

      // ALWAYS default to mock if undefined OR empty
      const data = res?.data?.rfqs;

      if (Array.isArray(data) && data.length > 0) {
        setRfqs(data);
      } else {
        console.warn("RFQ API returned empty — using mock data.");
        setRfqs(generateMockRFQs());
      }

    } catch (err) {
      console.warn("RFQ API unreachable — using mock data.");
      setRfqs(generateMockRFQs());
    }
  };

  loadRFQs();
}, []);

  // -----------------------------
  // Main Analysis Function
  // -----------------------------
  const analyzeRFQs = async () => {
    setAnalyzing(true);
    setAnalysisComplete(false);

    const prompt = `
You are a Fluxonix AI Procurement Optimization Agent.

Analyze the following RFQs and identify intelligent bundling opportunities to maximize:
- Supplier leverage
- Logistics efficiency
- Category synergy
- Deadline alignment
- Cost savings (5–15% typical)
- Risk mitigation (score 1–10)

RFQs:
${JSON.stringify(rfqs, null, 2)}

Respond ONLY in JSON:
{
  "bundles": [
    {
      "id": "BUNDLE-001",
      "name": "Bundle Name",
      "strategy": "Primary strategy",
      "rfqIds": ["RFQ-2025-001", ...],
      "totalValue": 123456,
      "estimatedSavings": 12000,
      "savingsPercentage": 7.5,
      "riskScore": 4,
      "benefits": ["..."],
      "considerations": ["..."]
    }
  ],
  "summary": {
    "totalRFQs": number,
    "bundledRFQs": number,
    "totalPotentialSavings": number,
    "recommendedApproach": "..."
  }
}`;

    try {
      // --------------------------------------------
      // 🔥 Fluxonix Standard: Inference Endpoint
      // --------------------------------------------
      const res = await api.post("/api/agent/run", {
        agent: "rfq_bundling_agent",
        prompt,
        response_format: "json",
      });

      const parsed = res.data.output;
      setBundles(parsed.bundles || []);
      setAnalysisComplete(true);
    } catch (err) {
      console.error("Fallback to rule-based bundling", err);
      setBundles(generateFallbackBundles());
      setAnalysisComplete(true);
    } finally {
      setAnalyzing(false);
    }
  };

  // -----------------------------
  // Simple fallback
  // -----------------------------
  const generateFallbackBundles = () => [
    {
      id: "BUNDLE-MOCK-001",
      name: "Textiles Synergy Bundle",
      strategy: "Category Synergy",
      rfqIds: ["RFQ-2025-001", "RFQ-2025-003"],
      totalValue: 305000,
      estimatedSavings: 21000,
      savingsPercentage: 7,
      riskScore: 3,
      benefits: ["Volume leverage", "Shared QA", "Aligned timelines"],
      considerations: ["Material differences require QA separation"],
    },
  ];

  const getRFQsByIds = (ids) => rfqs.filter((r) => ids.includes(r.id));

  const totalSavings = bundles.reduce(
    (sum, b) => sum + (b.estimatedSavings || 0),
    0
  );

  // -----------------------------
  // ⚛️ UI (Cleaned Up, Fluxonix Theme)
  // -----------------------------
  return (
    <div style={fx.page}>
      <div style={fx.card}>
        <header style={fx.header}>
          <div style={fx.headerLeft}>
            <Layers size={40} color={colors.primary} />
            <div>
              <h1 style={fx.h1}>RFQ Bundling Intelligence</h1>
              <p style={fx.subtitle}>
                AI-powered sourcing optimization by Fluxonix
              </p>
            </div>
          </div>

          <button
            disabled={analyzing}
            onClick={analyzeRFQs}
            style={analyzing ? fx.buttonDisabled : fx.button}
          >
            {analyzing ? "Analyzing..." : "Analyze RFQs"}
          </button>
        </header>

        <div style={fx.gridContainer}>
          {/* RFQ List */}
          <div style={fx.panelRFQ}>
            <h3 style={fx.panelTitle}>
              <Package size={22} />
              Active RFQs ({(rfqs || []).length})
            </h3>

            {rfqs.map((rfq) => (
              <div key={rfq.id} style={fx.rfqCard}>
                <div style={fx.cardTitle}>{rfq.component}</div>
                <div style={fx.cardSubtitle}>{rfq.category}</div>

                <div style={fx.metaRow}>
                  <span style={fx.metaItem}>
                    <MapPin size={14} /> {rfq.location}
                  </span>
                  <span style={fx.metaItem}>
                    <Calendar size={14} />{" "}
                    {new Date(rfq.deadline).toLocaleDateString()}
                  </span>
                  <span style={fx.metaItem}>
                    <DollarSign size={14} /> ${rfq.estimatedValue.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Bundles */}
          <div style={fx.panelBundles}>
            <h3 style={fx.panelTitleDanger}>
              <Sparkles size={22} />
              Recommended Bundles
            </h3>

            {analysisComplete &&
              bundles.map((bundle) => (
                <div
                  key={bundle.id}
                  style={fx.bundleCard}
                  onClick={() =>
                    setSelectedBundle(selectedBundle === bundle.id ? null : bundle.id)
                  }
                >
                  <div style={fx.cardTitle}>{bundle.name}</div>
                  <div style={fx.cardSubtitle}>{bundle.strategy}</div>

                  <div style={fx.statsGrid}>
                    <div style={fx.statBox}>
                      <div style={fx.statLabel}>Value</div>
                      <div style={fx.statValue(colors.primary)}>
                        ${bundle.totalValue.toLocaleString()}
                      </div>
                    </div>

                    <div style={fx.statBox}>
                      <div style={fx.statLabel}>Savings</div>
                      <div style={fx.statValue(colors.success)}>
                        ${bundle.estimatedSavings.toLocaleString()}
                      </div>
                    </div>

                    <div style={fx.statBox}>
                      <div style={fx.statLabel}>Risk</div>
                      <div style={fx.statValue(colors.warning)}>
                        {bundle.riskScore}/10
                      </div>
                    </div>
                  </div>

                  {selectedBundle === bundle.id && (
                    <div style={fx.expandedSection}>
                      <div style={fx.sectionTitle}>RFQs Included:</div>
                      <ul style={fx.list}>
                        {getRFQsByIds(bundle.rfqIds).map((r) => (
                          <li key={r.id} style={fx.listItem}>
                            {r.id} — {r.component}
                          </li>
                        ))}
                      </ul>

                      <div style={fx.sectionTitle}>Benefits:</div>
                      <ul style={fx.list}>
                        {bundle.benefits.map((b, i) => (
                          <li key={i} style={fx.listItem}>
                            {b}
                          </li>
                        ))}
                      </ul>

                      <div style={fx.sectionTitle}>Considerations:</div>
                      <ul style={fx.list}>
                        {bundle.considerations.map((c, i) => (
                          <li key={i} style={fx.listItem}>
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}

            {!analysisComplete && (
              <div style={fx.emptyState}>
                Click <strong>Analyze RFQs</strong> to generate bundles.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFQBundlingAgent;